import { useCallback, useEffect, useState } from "react";
import api, { qs, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { monthOptions, monthLabel } from "@/lib/dpc";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ScopePicker, SERVICE_LABELS } from "@/components/overview/ScopePicker";
import { PeriodControl, AsOfBanner } from "@/components/overview/PeriodControl";
import { CallOuts } from "@/components/overview/WeekStrip";
import { WarrantyToggle, useExWarranty } from "@/components/WarrantyToggle";
import { fmtP, heatCell } from "@/components/overview/Momentum";
import { SvcHeader } from "@/components/service-overview/SvcHeader";
import { SvcTiles } from "@/components/service-overview/SvcTiles";
import { SvcChart } from "@/components/service-overview/SvcChart";
import { WorkshopTable } from "@/components/service-overview/WorkshopTable";
import { CategoryTable, ClassTable } from "@/components/service-overview/SvcTables";
import { SvcMatrix, WorkshopPeers } from "@/components/service-overview/SvcMatrix";
import { SvcWeekStrip } from "@/components/service-overview/SvcWeekStrip";
import { BayPanel } from "@/components/service-overview/BayPanel";
import { fmtL } from "@/components/service-overview/SvcBits";

// Week view only: compact W1–W4 Total GS Ach % row so the month context stays visible.
function WeekSummary({ weeks, selected, onPick }) {
  return (
    <div className="rounded-md border bg-card px-3 py-2 flex flex-wrap items-center gap-2 text-xs" data-testid="sov-week-summary">
      <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mr-1">Total GS Ach % by week</span>
      {weeks.map((w) => {
        const c = w.cells.total_gs;
        const sel = w.week === selected;
        return (
          <button key={w.week} onClick={() => onPick(w.week)} className={`inline-flex items-center gap-1.5 rounded border px-2 py-1 transition-colors ${sel ? "border-foreground ring-1 ring-foreground/40 bg-secondary" : "hover:bg-secondary/60"} ${w.future ? "text-muted-foreground" : ""}`} title={`${w.week} · ${w.start}–${w.end}`} data-testid={`sov-week-summary-${w.week}`}>
            <span className="font-heading font-bold">{w.week}</span>
            {w.current && <span className="rounded bg-amber-200 text-amber-900 px-1 text-[9px] font-bold uppercase">now</span>}
            <span className={`num font-semibold rounded px-1 ${w.future ? "" : heatCell(c.pct)}`} data-testid={`sov-week-summary-${w.week}-pct`}>{w.future ? "—" : fmtP(c.pct)}</span>
            <span className="text-[10px] text-muted-foreground num">{w.future ? `target ₹${fmtL(c.plan)}` : `₹${fmtL(c.ach)} / ₹${fmtL(c.plan)}`}</span>
          </button>
        );
      })}
    </div>
  );
}

// Block A GS | BP | Total state: remembered per user for the browser session, back to GS on the next login (cleared in AppContext.login).
const A_MODE_KEY = "sov-a-mode";
const readAMode = (uid) => {
  try {
    const v = JSON.parse(sessionStorage.getItem(A_MODE_KEY) || "null");
    return v && v.uid === uid && ["gs", "bp", "total"].includes(v.mode) ? v.mode : "gs";
  } catch { return "gs"; }
};

export default function ServiceOverview() {
  const { user } = useApp();
  const [month, setMonth] = useState(monthOptions(1, 0)[0]);
  const [period, setPeriod] = useState("month");
  const [asOf, setAsOf] = useState("");
  const [zone, setZone] = useState("");
  const [workshop, setWorkshop] = useState("");
  const [ex, setEx] = useExWarranty();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aMode, setAModeState] = useState(() => readAMode(user?.id));
  const setAMode = (m) => { setAModeState(m); sessionStorage.setItem(A_MODE_KEY, JSON.stringify({ uid: user?.id, mode: m })); };

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/service/overview?${qs({ month, period, as_of: asOf, zone, workshop, ex_warranty: ex })}`).then(({ data }) => setData(data)).catch((e) => toast.error(apiError(e), { id: "sov-error" })).finally(() => setLoading(false));
  }, [month, period, asOf, zone, workshop, ex]);
  useEffect(load, [load]);

  const changeMonth = (m) => { setMonth(m); setPeriod("month"); setAsOf(""); };
  const h = data?.header;
  const per = h?.period;
  const isWeek = per?.kind === "week";
  const wsScope = h?.scope_kind === "workshop";
  const title = h?.breadcrumb?.join(" › ") || (user?.role === "manager" ? "My workshop" : user?.role === "zonal" ? "My zone" : "Group");
  return (
    <div className="space-y-4" data-testid="service-overview">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">Service Group Overview <span className="text-muted-foreground font-medium text-base" data-testid="sov-title-scope">· {title}{isWeek ? ` · ${per.week}` : ""}</span></h1>
          <p className="text-sm text-muted-foreground">Targets, achievement and momentum of the whole service business on one screen — {isWeek ? `${per.week} of ${monthLabel(month)}` : "this month"}</p>
        </div>
        <div className="flex flex-wrap items-start gap-2" data-testid="sov-controls">
          <ScopePicker picker={data?.picker} region={zone} dealer={workshop} breadcrumb={h?.breadcrumb} labels={SERVICE_LABELS} onChange={({ region: r, dealer: d }) => { setZone(r || ""); setWorkshop(d || ""); }} />
          <Select value={month} onValueChange={changeMonth}>
            <SelectTrigger className="h-8 w-[120px] text-xs" data-testid="sov-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>{monthOptions(13, 0).map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
          </Select>
          <PeriodControl period={period} setPeriod={setPeriod} weeks={h?.weeks} month={month} asOf={asOf} setAsOf={setAsOf} />
          <WarrantyToggle ex={ex} onChange={setEx} />
        </div>
      </div>

      <AsOfBanner asOf={asOf} onClear={() => setAsOf("")} />

      {!data && <div className="text-sm text-muted-foreground py-20 text-center" data-testid="sov-loading">Loading service overview…</div>}
      {data && (
        <div className={`space-y-4 transition-opacity ${loading ? "opacity-60" : ""}`} data-period={per?.kind} data-scope={h.scope_kind}>
          <SvcHeader h={h} />
          {isWeek && <WeekSummary weeks={data.weeks} selected={per.week} onPick={setPeriod} />}
          <div className="grid gap-4 2xl:grid-cols-5 [&>*]:min-w-0">
            <div className="2xl:col-span-3 min-w-0"><SvcTiles tiles={data.tiles} tilesBp={data.tiles_bp} tilesTotal={data.tiles_total} period={per} exWarranty={h.ex_warranty} mode={aMode} onMode={setAMode} /></div>
            <div className="2xl:col-span-2 min-w-0"><SvcChart chart={data.chart} asOf={h.as_of} period={per} exWarranty={h.ex_warranty} /></div>
          </div>
          {wsScope ? (
            <>
              <SvcMatrix matrix={data.matrix} workshopName={data.picker?.dealers?.find((d) => d.code === h.workshop)?.name || ""} period={per} />
              <WorkshopPeers rows={data.rows} me={h.workshop} isWeek={isWeek} />
            </>
          ) : (
            <WorkshopTable rows={data.rows} zones={data.zones} total={data.total} zoneLabels={data.zone_labels} isManager={h.is_manager} period={per} exWarranty={h.ex_warranty} onFocus={(code) => { setWorkshop(code); setZone(""); }} />
          )}
          <div className="grid gap-4 xl:grid-cols-2 [&>*]:min-w-0">
            <CategoryTable rows={data.categories} period={per} scopeKind={h.scope_kind} />
            <ClassTable rows={data.classes} period={per} />
          </div>
          {isWeek ? (
            <CallOuts items={data.callouts} period={per} />
          ) : (
            <div className="grid gap-4 xl:grid-cols-5 [&>*]:min-w-0">
              <div className="xl:col-span-3"><SvcWeekStrip weeks={data.weeks} /></div>
              <div className="xl:col-span-2"><CallOuts items={data.callouts} period={per} /></div>
            </div>
          )}
          <BayPanel bays={data.bays} title={`H · Bay productivity${isWeek ? ` — ${per.week}` : " — month-to-date"}`} />
        </div>
      )}
    </div>
  );
}
